import { format, parse } from 'date-fns';
import moment from 'moment';
import { DATE_FORMAT, MONTH_FORMAT, TIME_FORMAT } from '../models';

// Format Docs: https://date-fns.org/v1.28.5/docs/format

export class DateTimeUtils {
  public static formatDateToString(
    d: Date,
    targetFormat: DATE_FORMAT | MONTH_FORMAT
  ): string {
    return format(d, targetFormat);
  }

  public static formatTimeToString(d: Date, withoutSecond?: boolean): string {
    return withoutSecond
      ? format(d, TIME_FORMAT.TIME_FORMAT_WITHOUT_SECOND)
      : format(d, TIME_FORMAT.TIME_FORMAT);
  }

  public static formatDateTimeToString(
    d: Date,
    targetFormat: DATE_FORMAT
  ): string {
    if (targetFormat === DATE_FORMAT.DATE_FORMAT_BACKEND) {
      return `${DateTimeUtils.formatDateToString(
        d,
        targetFormat
      )}T${DateTimeUtils.formatTimeToString(d)}`;
    } else {
      return `${DateTimeUtils.formatDateToString(
        d,
        targetFormat
      )} ${DateTimeUtils.formatTimeToString(d, true)}`;
    }
  }

  public static formatTimeWithSecondToHourMinute(
    timeWithSecond: string
  ): string {
    const pattern = 'HH:mm';

    if (moment(timeWithSecond, pattern, true).isValid()) {
      return timeWithSecond;
    }

    const arr = timeWithSecond?.split(':');
    const formattedValue = `${arr?.[0]}:${arr?.[1]}`;

    if (moment(formattedValue, pattern, true).isValid()) {
      return formattedValue;
    }

    return null;
  }

  public static formatHourMinuteToTimeWithSecond(
    hourMinuteTime: string
  ): string {
    const pattern = 'HH:mm:ss';

    if (moment(hourMinuteTime, pattern, true).isValid()) {
      return hourMinuteTime;
    }
    const formattedValue = `${hourMinuteTime}:00`;

    if (moment(formattedValue, pattern, true).isValid()) {
      return formattedValue;
    }

    return null;
  }

  public static isSameDate(d1: Date, d2: Date): boolean {
    return (
      this.formatDateToString(d1, DATE_FORMAT.DATE_FORMAT_BACKEND) ===
      this.formatDateToString(d2, DATE_FORMAT.DATE_FORMAT_BACKEND)
    );
  }

  public static isSameTime(
    d1: Date,
    d2: Date,
    withoutSecond: boolean
  ): boolean {
    return (
      this.formatTimeToString(d1, withoutSecond) ===
      this.formatTimeToString(d2, withoutSecond)
    );
  }

  public static isDateInRange(
    d: Date,
    fromDate: Date,
    toDate: Date,
    excludeFromDateAndToDate = false
  ): boolean {
    const dStr = DateTimeUtils.formatDateToString(
      d,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const fromDateStr = DateTimeUtils.formatDateToString(
      fromDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const toDateStr = DateTimeUtils.formatDateToString(
      toDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );

    if (excludeFromDateAndToDate) {
      return dStr > fromDateStr && dStr < toDateStr;
    }
    return dStr >= fromDateStr && dStr <= toDateStr;
  }

  public static isDateTimeInRange(
    d: Date,
    fromDate: Date,
    toDate: Date,
    excludeFromDateAndToDate = false
  ): boolean {
    const dStr = DateTimeUtils.formatDateTimeToString(
      d,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const fromDateStr = DateTimeUtils.formatDateTimeToString(
      fromDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const toDateStr = DateTimeUtils.formatDateTimeToString(
      toDate,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );

    if (excludeFromDateAndToDate) {
      return dStr > fromDateStr && dStr < toDateStr;
    }
    return dStr >= fromDateStr && dStr <= toDateStr;
  }

  public static areTwoDateRangesOverlap(
    d1FromDate: Date,
    d1ToDate: Date,
    d2FromDate: Date,
    d2ToDate: Date
  ): boolean {
    if (
      DateTimeUtils.isSameTime(d1FromDate, d2FromDate, true) &&
      DateTimeUtils.isSameTime(d1ToDate, d2ToDate, true)
    ) {
      return true;
    }
    return (
      DateTimeUtils.isDateTimeInRange(d1FromDate, d2FromDate, d2ToDate, true) ||
      DateTimeUtils.isDateTimeInRange(d1ToDate, d2FromDate, d2ToDate, true) ||
      DateTimeUtils.isDateTimeInRange(d2FromDate, d1FromDate, d1ToDate, true) ||
      DateTimeUtils.isDateTimeInRange(d2ToDate, d1FromDate, d1ToDate, true)
    );
  }

  public static parseStringToDate(dateTime: string): Date {
    if (!dateTime) {
      return null;
    }

    return parse(dateTime);
  }

  public static generateDateFromDateAndTimeString(
    date: string,
    time: string
  ): Date {
    const dateObj = DateTimeUtils.parseStringToDate(date);

    const timeArr = time?.split(':');

    try {
      if (timeArr?.length > 1) {
        dateObj.setHours(Number(timeArr?.[0]));
        dateObj.setMinutes(Number(timeArr?.[1]));
        dateObj.setSeconds(Number(timeArr?.[2]));
      }
    } catch (e) {
      // no need to log
    }

    return dateObj;
  }

  public static getBeginOfDate(d: Date): Date {
    if (!d) return null;
    d.setHours(0, 0, 0, 0);
    return d;
  }

  public static getEndOfDate(d: Date): Date {
    if (!d) return null;
    d.setHours(23, 59, 59, 999);
    return d;
  }

  public static getFirstDateOfWeek(d?: Date): Date {
    return moment(d || new Date())
      .startOf('isoWeek')
      .toDate();
  }

  public static getFirstDateOfPreviousWeek(d?: Date): Date {
    return moment(d || new Date())
      .subtract(1, 'week')
      .startOf('isoWeek')
      .toDate();
  }

  public static getFirstDateOfNextWeek(d?: Date): Date {
    return moment(d || new Date())
      .add(1, 'week')
      .startOf('isoWeek')
      .toDate();
  }

  public static getFirstDateOfMonth(d?: Date): Date {
    return moment(d || new Date())
      .startOf('month')
      .toDate();
  }

  public static getFirstDateOfPreviousMonth(d?: Date): Date {
    return moment(d || new Date())
      .subtract(1, 'month')
      .startOf('month')
      .toDate();
  }

  public static getFirstDateOfNextMonth(d?: Date): Date {
    return moment(d || new Date())
      .add(1, 'month')
      .startOf('month')
      .toDate();
  }

  public static getFirstDateOfQuarter(d?: Date): Date {
    return moment(d || new Date())
      .startOf('quarter')
      .toDate();
  }

  public static getFirstDateOfPreviousQuarter(d?: Date): Date {
    return moment(d || new Date())
      .subtract(1, 'quarter')
      .startOf('quarter')
      .toDate();
  }

  public static getFirstDateOfYear(d?: Date): Date {
    return moment(d || new Date())
      .startOf('year')
      .toDate();
  }

  public static getFirstDateOfPreviousYear(d?: Date): Date {
    return moment(d || new Date())
      .subtract(1, 'year')
      .startOf('year')
      .toDate();
  }

  public static getLastDateOfWeek(d?: Date): Date {
    return moment(d || new Date())
      .endOf('isoWeek')
      .toDate();
  }

  public static getLastDateOfPreviousWeek(d?: Date): Date {
    return moment(d || new Date())
      .subtract(1, 'week')
      .endOf('isoWeek')
      .toDate();
  }

  public static getLastDateOfNextWeek(d?: Date): Date {
    return moment(d || new Date())
      .add(1, 'week')
      .endOf('isoWeek')
      .toDate();
  }

  public static getLastDateOfMonth(d?: Date): Date {
    return moment(d || new Date())
      .endOf('month')
      .toDate();
  }

  public static getLastDateOfPreviousMonth(d?: Date): Date {
    return moment(d || new Date())
      .subtract(1, 'month')
      .endOf('month')
      .toDate();
  }

  public static getLastDateOfNextMonth(d?: Date): Date {
    return moment(d || new Date())
      .add(1, 'month')
      .endOf('month')
      .toDate();
  }

  public static getLastDateOfQuarter(d?: Date): Date {
    return moment(d || new Date())
      .endOf('quarter')
      .toDate();
  }

  public static getLastDateOfPreviousQuarter(d?: Date): Date {
    return moment(d || new Date())
      .subtract(1, 'quarter')
      .endOf('quarter')
      .toDate();
  }

  public static getLastDateOfYear(d?: Date): Date {
    return moment(d || new Date())
      .endOf('year')
      .toDate();
  }

  public static getLastDateOfPreviousYear(d?: Date): Date {
    return moment(d || new Date())
      .subtract(1, 'year')
      .endOf('year')
      .toDate();
  }

  public static addDayToDate(d: Date, diffDays = 0): Date {
    if (!d) {
      return null;
    }

    return moment(d).add(diffDays, 'day').toDate();
  }

  public static addHourToDate(d: Date, diffHours = 0): Date {
    if (!d) {
      return null;
    }

    return moment(d).add(diffHours, 'hour').toDate();
  }

  public static addMinuteToDate(d: Date, diffMinutes = 0): Date {
    if (!d) {
      return null;
    }

    return moment(d).add(diffMinutes, 'm').toDate();
  }

  public static getDiffInSecond(d1: Date, d2: Date): number {
    const d1Moment = moment(d1);
    const d2Moment = moment(d2);

    return d1Moment.diff(d2Moment, 'seconds');
  }

  public static getDiffInMinute(d1: Date, d2: Date): number {
    const d1Moment = moment(d1);
    const d2Moment = moment(d2);

    // result = d1 - d2
    return d1Moment.diff(d2Moment, 'minutes');
  }

  public static getListOfDateString(
    fromDate: string,
    toDate: string
  ): string[] {
    const listOfDate = [];

    let runnerAsObj = this.parseStringToDate(fromDate);
    let runnerAsStr = `${fromDate}`;

    while (runnerAsStr <= toDate) {
      if (listOfDate.includes(runnerAsStr)) {
        break;
      }

      listOfDate.push(runnerAsStr);

      runnerAsObj = this.addDayToDate(runnerAsObj, 1);
      runnerAsStr = this.formatDateToString(
        runnerAsObj,
        DATE_FORMAT.DATE_FORMAT_BACKEND
      );
    }

    return listOfDate;
  }

  public static getListOfDate(fromDate: Date, toDate: Date): Date[] {
    const listOfDateStr = this.getListOfDateString(
      this.formatDateToString(fromDate, DATE_FORMAT.DATE_FORMAT_BACKEND),
      this.formatDateToString(toDate, DATE_FORMAT.DATE_FORMAT_BACKEND)
    );

    return listOfDateStr?.map((dateStr) => this.parseStringToDate(dateStr));
  }
}
